/**
 * Wire-контракт `ResourceDto` (Platform.Contracts) и его проверка на границе
 * (D-056). Тип TypeScript ответа сам не проверяет — проверяет декодер, и
 * неожиданный ответ становится ошибкой `invalid-response`, а не пустым экраном.
 *
 * Список (`decodeResource`) `settings` НАМЕРЕННО отбрасывает: native-настройки
 * показывает и правит форма пакета вида, а не общий список. Их несёт только
 * `decodeResourceDetails` — ответ одного ресурса для формы правки. Секрет
 * Platform не возвращает никогда; сюда он попасть и не может.
 */
import { IMPLICIT_MODULE, type ConnectorManifest, type ConnectorModule } from '../model/connectors'
import type {
  MyResource,
  ResourceContact,
  ResourceContactError,
  ResourceContactState,
  ResourceDetails,
  ResourceDraft,
  ResourceSummary,
} from '../model/types'

export interface ResourceCapabilityWire {
  name: string
  enabled: boolean
}

/** `CreateResourceRequest`: секрет — только при вводе, иначе поле не передаётся. */
export interface CreateResourceWire {
  kind: string
  displayName: string
  contractVersion: string
  capabilities: ResourceCapabilityWire[]
  settings: Record<string, unknown>
  secret?: string
}

/** `UpdateResourceRequest`: `rotatedSecret` отсутствует — секрет не меняется. */
export interface UpdateResourceWire {
  displayName: string
  enabled: boolean
  capabilities: ResourceCapabilityWire[]
  settings: Record<string, unknown>
  rotatedSecret?: string
}

/** Исход проверки одного модуля вида (RP-OWN13, ADR-22): идентификатор модуля из манифеста вида и тот же статус и код. */
export interface ProbeModuleResult {
  id: string
  status: string
  errorCode: string | null
}

/**
 * `ResourceProbeResult` Platform. `modules` — исходы по модулям вида; пока Platform их не присылает, поля нет, и клиент
 * не выдумывает состояние модулей по общему итогу.
 */
export interface ProbeResult {
  status: string
  errorCode: string | null
  modules?: readonly ProbeModuleResult[]
}

function fail(message: string): never {
  throw new Error(message)
}

function text(value: unknown, field: string): string {
  return typeof value === 'string' && value.trim() !== '' ? value : fail(`поле ${field} — не строка`)
}

function idOf(value: unknown): string {
  if (typeof value === 'string') return text(value, 'id')
  if (typeof value === 'object' && value !== null) return text((value as { value?: unknown }).value, 'id.value')
  return fail('поле id отсутствует')
}

function objectOf(raw: unknown, what: string): Record<string, unknown> {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) fail(`${what} — не объект`)
  return raw as Record<string, unknown>
}

/** Ревизия настроек: строка; поля нет (служба до AU1b) — `null`, а не ошибка. */
function revisionOf(value: unknown): string | null {
  if (value === undefined || value === null) return null
  return text(value, 'configurationVersion')
}

function timeOf(value: unknown, field: string): string | null {
  if (value === undefined || value === null) return null
  return text(value, field)
}

function optionalText(value: unknown, field: string): string | null {
  if (value === undefined || value === null) return null
  return typeof value === 'string' ? value : fail(`поле ${field} — не строка`)
}

const CONTACT_STATES: readonly string[] = ['ok', 'error', 'unknown']

function contactErrorOf(raw: unknown): ResourceContactError | null {
  if (raw === undefined || raw === null) return null
  const error = objectOf(raw, 'contact.lastError')
  if (error.code !== undefined && error.code !== null && typeof error.code !== 'number') fail('поле contact.lastError.code — не число')
  return {
    at: text(error.at, 'contact.lastError.at'),
    kind: text(error.kind, 'contact.lastError.kind'),
    code: typeof error.code === 'number' ? error.code : null,
    message: optionalText(error.message, 'contact.lastError.message') ?? '',
  }
}

/** Связь с источником (SRC-HEALTH); поля нет — `null`: Platform до SRC-HEALTH. */
export function decodeContact(raw: unknown): ResourceContact | null {
  if (raw === undefined || raw === null) return null
  const contact = objectOf(raw, 'contact')
  const state = text(contact.state, 'contact.state')
  // Новое состояние сервера, которого клиент не знает, — «неизвестно», а не пустой экран.
  return {
    state: (CONTACT_STATES.includes(state) ? state : 'unknown') as ResourceContactState,
    lastRequestAt: timeOf(contact.lastRequestAt, 'contact.lastRequestAt'),
    lastResponseAt: timeOf(contact.lastResponseAt, 'contact.lastResponseAt'),
    lastSuccessAt: timeOf(contact.lastSuccessAt, 'contact.lastSuccessAt'),
    lastError: contactErrorOf(contact.lastError),
    reasonCode: optionalText(contact.reasonCode, 'contact.reasonCode'),
    reason: optionalText(contact.reason, 'contact.reason'),
  }
}

export function decodeResource(raw: unknown): ResourceSummary {
  const item = objectOf(raw, 'ресурс')
  if (typeof item.enabled !== 'boolean') fail('поле enabled — не логическое')
  if (!Array.isArray(item.capabilities)) fail('поле capabilities — не массив')
  return {
    id: idOf(item.id),
    kind: text(item.kind, 'kind'),
    displayName: text(item.displayName, 'displayName'),
    enabled: item.enabled,
    contractVersion: text(item.contractVersion, 'contractVersion'),
    configurationVersion: revisionOf(item.configurationVersion),
    capabilities: item.capabilities.map((capability: unknown, index) => {
      if (typeof capability !== 'object' || capability === null) fail(`capabilities[${index}] — не объект`)
      const entry = capability as Record<string, unknown>
      if (typeof entry.enabled !== 'boolean') fail(`capabilities[${index}].enabled — не логическое`)
      return { name: text(entry.name, `capabilities[${index}].name`), enabled: entry.enabled }
    }),
    // Связь есть только в карточке ресурса; в списке реестра поля нет или оно null.
    ...(item.contact === undefined ? {} : { contact: decodeContact(item.contact) }),
  }
}

export function decodeResources(raw: unknown): ResourceSummary[] {
  if (!Array.isArray(raw)) fail('список ресурсов — не массив')
  return raw.map(decodeResource)
}

/** Мои ресурсы с правом (`me/resources`): массив `MyResourceDto`. */
export function decodeMyResources(raw: unknown): MyResource[] {
  if (!Array.isArray(raw)) fail('мои ресурсы — не массив')
  return raw.map((entry: unknown, index) => {
    const item = objectOf(entry, `ресурс [${index}]`)
    return {
      id: idOf(item.id),
      kind: text(item.kind, 'kind'),
      displayName: text(item.displayName, 'displayName'),
      configurationVersion: revisionOf(item.configurationVersion),
      contact: decodeContact(item.contact),
    }
  })
}

/** Один ресурс с native-настройками (JSON по схеме вида). Отсутствующие настройки — пустой объект. */
export function decodeResourceDetails(raw: unknown): ResourceDetails {
  const summary = decodeResource(raw)
  const settings = objectOf(raw, 'ресурс').settings
  return { ...summary, settings: settings === undefined || settings === null ? {} : objectOf(settings, 'settings') }
}

function errorCodeOf(value: unknown, field: string): string | null {
  if (value === undefined || value === null) return null
  return typeof value === 'string' ? value : fail(`поле ${field} — не строка`)
}

export function decodeProbeResult(raw: unknown): ProbeResult {
  const item = objectOf(raw, 'итог проверки')
  const result = { status: text(item.status, 'status'), errorCode: errorCodeOf(item.errorCode, 'errorCode') }
  if (item.modules === undefined || item.modules === null) return result
  if (!Array.isArray(item.modules)) fail('поле modules — не массив')
  return {
    ...result,
    modules: item.modules.map((entry: unknown, index): ProbeModuleResult => {
      const part = objectOf(entry, `modules[${index}]`)
      return {
        id: text(part.id, `modules[${index}].id`),
        status: text(part.status, `modules[${index}].status`),
        errorCode: errorCodeOf(part.errorCode, `modules[${index}].errorCode`),
      }
    }),
  }
}

function namesOf(raw: unknown, field: string): string[] {
  if (!Array.isArray(raw)) return fail(`поле ${field} — не массив`)
  const names = raw.map((name: unknown, index) => text(name, `${field}[${index}]`))
  return new Set(names).size === names.length ? names : fail(`в поле ${field} есть повтор`)
}

function decodeModule(id: string, raw: unknown): ConnectorModule {
  const item = objectOf(raw, `modules.${id}`)
  return {
    id,
    capabilities: namesOf(item.capabilities, `modules.${id}.capabilities`),
    contracts: item.contracts === undefined || item.contracts === null ? [] : namesOf(item.contracts, `modules.${id}.contracts`),
  }
}

/**
 * Манифест вида (та же форма, что у `<вид>.manifest.json` Platform): код, версия договора, права и раздел `modules` —
 * объект «код модуля → права и договоры» (ADR-22). Вид без раздела (1С, AQSI) — один неявный модуль `main` со всеми правами;
 * модуль с правом, которого нет у вида, право у двух модулей или вне модулей — отказ: ответ сервера противоречив.
 */
export function decodeConnector(raw: unknown): ConnectorManifest {
  const item = objectOf(raw, 'вид ресурса')
  const kind = text(item.kind, 'kind')
  const capabilities = namesOf(item.capabilities, 'capabilities')
  const declared = item.modules === undefined || item.modules === null ? {} : objectOf(item.modules, 'modules')
  const modules: ConnectorModule[] =
    Object.keys(declared).length === 0
      ? [{ id: IMPLICIT_MODULE, capabilities, contracts: [] }]
      : Object.entries(declared).map(([id, part]) => decodeModule(id, part))
  const known = new Set(capabilities)
  const owners = new Map<string, string>()
  for (const part of modules) {
    for (const name of part.capabilities) {
      if (!known.has(name)) fail(`модуль «${part.id}» вида «${kind}» называет право «${name}», которого у вида нет`)
      const owner = owners.get(name)
      if (owner !== undefined) fail(`право «${name}» вида «${kind}» числится и за модулем «${owner}», и за «${part.id}»`)
      owners.set(name, part.id)
    }
  }
  const orphan = capabilities.find((name) => !owners.has(name))
  if (orphan !== undefined) fail(`право «${orphan}» вида «${kind}» не принадлежит ни одному модулю`)
  return { kind, contractVersion: text(item.contractVersion, 'contractVersion'), capabilities, modules }
}

/** Установленные виды ресурсов (`GET connectors`): массив манифестов; повтор вида — отказ. */
export function decodeConnectors(raw: unknown): ConnectorManifest[] {
  if (!Array.isArray(raw)) fail('список видов — не массив')
  const items = raw.map(decodeConnector)
  if (new Set(items.map((item) => item.kind)).size !== items.length) fail('вид ресурса пришёл дважды')
  return items
}

const capabilitiesWire = (draft: ResourceDraft): ResourceCapabilityWire[] =>
  draft.capabilities.map((capability) => ({ name: capability.name, enabled: capability.enabled }))

export function createResourceWire(
  kind: string,
  contractVersion: string,
  draft: ResourceDraft,
  secret: string | undefined,
): CreateResourceWire {
  return {
    kind,
    displayName: draft.displayName.trim(),
    contractVersion,
    capabilities: capabilitiesWire(draft),
    settings: { ...draft.settings },
    ...(secret !== undefined ? { secret } : {}),
  }
}

export function updateResourceWire(draft: ResourceDraft, secret: string | undefined): UpdateResourceWire {
  return {
    displayName: draft.displayName.trim(),
    enabled: draft.enabled,
    capabilities: capabilitiesWire(draft),
    settings: { ...draft.settings },
    ...(secret !== undefined ? { rotatedSecret: secret } : {}),
  }
}
